import pluralize from 'pluralize';
import { Icons, mergeSx, Typography, Stack, tokens } from '@cast/design-system';

import {
  useInitializeTrialDialog,
  useReportingTrial,
  useTrial,
} from '@cast/shared-trial-ui';
import { useIsUserTriggeredDemoMode } from '@cast/infrastructure-demo';

import { useOnboardClusterDialog } from 'hooks/dialogs';
import { useIsOrganizationReadOnly } from 'hooks/useIsOrganizationReadOnly';
import { useTrialHasEndedDialog } from './dialogs/useTrialHasEndedDialog';

import { useTrialInProgressDialog } from './dialogs/useTrialInProgressDialog';
import type { StackProps } from '@cast/design-system';

type BadgeBoxProps = StackProps & {
  text: string;
  icon: React.ReactNode;
};

const BadgeBox = ({ text, icon, sx, ...props }: BadgeBoxProps) => {
  return (
    <Stack
      data-testid="trial-badge"
      direction="row"
      flexWrap="nowrap"
      alignItems="center"
      sx={mergeSx(
        {
          borderRadius: '4px',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'currentColor',
          color: tokens.theme.fg.status.success,
          backgroundColor: tokens.theme.bg.status.successSubtle,
          padding: '2px 8px',
          gap: '4px',
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.8,
          },
        },
        sx
      )}
      {...props}
    >
      <Typography variant="P14M" color="inherit">
        {text}
      </Typography>

      {icon}
    </Stack>
  );
};

export const TrialBadge = () => {
  const accountReadOnly = useIsOrganizationReadOnly();
  const isUserTriggeredDemoMode = useIsUserTriggeredDemoMode();

  const {
    isEligible,
    isAboutToExpire,
    state,
    expiresInDays,
    expiresInDaysGracePeriod,
  } = useTrial();
  const { isEligible: isReportingEligible, isLoading: isReportingLoading } =
    useReportingTrial();
  const { openDialog: openInitializeTrialDialog } = useInitializeTrialDialog();
  const { openDialog: openTrialInProgressDialog } = useTrialInProgressDialog();
  const { openDialog: openTrialHasEndedDialog } = useTrialHasEndedDialog();
  const { openOnboardClusterDialog } = useOnboardClusterDialog();

  // Reporting monetization owns trial UX for eligible orgs — never PLG "Start free trial".
  // Wait for reporting status so loading isEligible:false is not treated as confirmed ineligibility.
  const shouldBadgeBeVisible =
    !isReportingLoading &&
    !isReportingEligible &&
    !isUserTriggeredDemoMode &&
    !accountReadOnly &&
    isEligible;

  if (!shouldBadgeBeVisible) {
    return null;
  }

  if (state === 'post-trial') {
    return (
      <BadgeBox
        text="Trial expired"
        icon={<Icons.Clock weight="bold" />}
        sx={{
          color: tokens.theme.fg.status.error,
          backgroundColor: tokens.theme.bg.status.errorSubtle,
        }}
        onClick={() => openTrialHasEndedDialog()}
      />
    );
  }

  if (state === 'grace-period') {
    return (
      <BadgeBox
        text={
          expiresInDaysGracePeriod === 0
            ? 'Grace period ends today'
            : `Grace period ends in ${expiresInDaysGracePeriod} ${pluralize(
                'day',
                expiresInDaysGracePeriod
              )}`
        }
        icon={<Icons.Clock weight="bold" />}
        sx={{
          color: tokens.theme.fg.status.error,
          backgroundColor: tokens.theme.bg.status.errorSubtle,
        }}
        onClick={() => openTrialHasEndedDialog()}
      />
    );
  }

  if (state === 'active') {
    if (isAboutToExpire) {
      return (
        <BadgeBox
          text={
            expiresInDays === 0
              ? 'Trial expires today'
              : `Trial expires in ${expiresInDays} ${pluralize(
                  'day',
                  expiresInDays
                )}`
          }
          icon={<Icons.Clock weight="bold" />}
          sx={{
            color: tokens.theme.fg.status.error,
            backgroundColor: tokens.theme.bg.status.errorSubtle,
          }}
          onClick={() => openTrialInProgressDialog()}
        />
      );
    }

    return (
      <BadgeBox
        text={`Trial: ${expiresInDays} ${pluralize('day', expiresInDays)} left`}
        icon={<Icons.Clock weight="bold" />}
        onClick={() => openTrialInProgressDialog()}
      />
    );
  }

  return (
    <BadgeBox
      text="Start free trial"
      icon={<Icons.Lightning weight="bold" />}
      onClick={() =>
        openInitializeTrialDialog(undefined, {
          onClose: (trialInitialized) => {
            if (trialInitialized === true) {
              openOnboardClusterDialog();
            }
          },
        })
      }
    />
  );
};
